import { useEffect, useState } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  submittingLabel?: string;
  errorLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  submittingLabel = "Working…",
  errorLabel = "The operation failed.",
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setError(undefined);
    }
  }, [open]);

  const confirm = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : errorLabel,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!submitting) {
          onOpenChange(nextOpen);
        }
      }}
      title={title}
      description={description}
      footer={
        <div className="ui-confirm-dialog__actions">
          <Button
            variant="ghost"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            disabled={submitting}
            onClick={() => void confirm()}
          >
            {submitting ? submittingLabel : confirmLabel}
          </Button>
        </div>
      }
    >
      {error ? <p className="ui-confirm-dialog__error" role="alert">{error}</p> : null}
    </Dialog>
  );
}
